import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * Static info page shell used by Terms & Conditions, Privacy Policy, Shipping Policy,
 * Return & Refund Policy, Contact Us. Retailers-style page layout: ivory bg,
 * centered max-w container, small red uppercase tracking-widest section label above the title.
 */
export default function InfoPage({ label, title, children, testid }) {
    return (
        <div className="min-h-screen bg-sdf-ivory" data-testid={testid}>
            <Header />
            <main className="max-w-3xl mx-auto px-4 py-8 sm:py-12 pb-20">
                {label && <div className="text-sdf-red font-bold text-xs tracking-widest">{label.toUpperCase()}</div>}
                <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-sdf-text">{title}</h1>
                {/* Policy content — typography tuned for clear hierarchy:
                    * h3 headings are noticeably larger, bold, well-spaced.
                    * paragraphs have comfortable line-height and spacing between points.
                    * consistent across every policy page via this shared shell. */}
                <div className="mt-6 card-soft p-6 sm:p-8 max-w-none text-sdf-text text-[15px] sm:text-base leading-7 sm:leading-8 [&>p]:mb-4 [&>p]:last:mb-0 [&>h3]:text-lg [&>h3]:sm:text-xl [&>h3]:font-extrabold [&>h3]:text-sdf-text [&>h3]:mt-7 [&>h3]:mb-2 [&>h3]:first:mt-0 [&>h3]:leading-tight">
                    {children}
                </div>
            </main>
            <Footer />
        </div>
    );
}

export function TermsPage() {
    return (
        <InfoPage label="Legal" title="Terms & Conditions" testid="page-terms">
            <p>These Terms &amp; Conditions govern the use of the Sri Durga Foods® Distribution portal (“Portal”). By registering as a distributor and using this Portal, you agree to be bound by these terms.</p>
            <h3>1. Eligibility</h3>
            <p>Only businesses approved by Sri Durga Foods® may use this Portal to place wholesale orders.</p>
            <h3>2. Ordering &amp; Payments</h3>
            <p>Orders are placed either on Cash on Delivery (COD) or on Credit Purchase terms as per the business agreement between the parties. Credit balances must be settled per the agreed schedule.</p>
            <h3>3. Pricing</h3>
            <p>All prices are wholesale and subject to change.</p>
            <h3>4. Cancellations</h3>
            <p>A distributor may request cancellation of an order only before it reaches the <b>Dispatched</b> stage. Once an order is Dispatched, it can no longer be cancelled by the distributor.</p>
            <p>To request a cancellation before dispatch, the distributor must contact Sri Durga Foods® directly over a phone call and share the relevant order details on WhatsApp. Our team will verify the request against the order and revert to the distributor. The cancellation will be finalised only after this verification and the distributor's explicit confirmation on the same conversation.</p>
            <h3>5. Contact</h3>
            <p>For any queries, please reach out via the Contact Us page or the Chat on WhatsApp option in the navigation menu.</p>
        </InfoPage>
    );
}

export function PrivacyPage() {
    return (
        <InfoPage label="Legal" title="Privacy Policy" testid="page-privacy">
            <p>Sri Durga Foods® Distribution respects the privacy of every distributor. This policy explains what information we collect and how we use it.</p>
            <h3>Information Collected</h3>
            <p>Business name, proprietor name, mobile number, complete address, PIN code, and optionally GSTIN, PAN and email. This information is used solely for order fulfilment and business operations.</p>
            <h3>Data Retention</h3>
            <p>Distributor and order records are retained for the duration required by applicable business and tax regulations.</p>
        </InfoPage>
    );
}

export function ShippingPage() {
    return (
        <InfoPage label="Logistics" title="Shipping Policy" testid="page-shipping">
            <p>Sri Durga Foods® Distribution ships to approved distributors across India through partner logistics networks and our own distribution vehicles.</p>
            <h3>Order Processing</h3>
            <p>Orders confirmed before 2 PM (IST) are typically processed the same working day. Larger consignments may take 1–2 working days for packing.</p>
            <h3>Delivery Timelines</h3>
            <p>Intra-city: 1–3 working days. Inter-city: 3–7 working days. Remote pin codes may take longer.</p>
            <h3>Freight</h3>
            <p>Freight may be included in the invoice or billed separately depending on the distributor agreement.</p>
            <h3>Handover &amp; POD</h3>
            <p>Proof of delivery (POD) will be shared with the distributor after the consignment is delivered.</p>
        </InfoPage>
    );
}

export function ReturnPage() {
    return (
        <InfoPage label="Support" title="Return & Refund Policy" testid="page-return">
            <p>We stand behind the quality of every product we ship.</p>
            <h3>Reporting Issues</h3>
            <p>Any damage, shortage or quality concern must be reported within 48 hours of delivery, with product images, batch details and the invoice number.</p>
            <h3>Replacement</h3>
            <p>Approved claims will be replaced in the next consignment or credited against future orders as mutually agreed.</p>
            <h3>Non-returnable</h3>
            <p>Products opened for retail counter display or altered from their sealed condition are not eligible for return.</p>
        </InfoPage>
    );
}

export function ContactPage() {
    return (
        <InfoPage label="Reach Us" title="Contact Us" testid="page-contact">
            <p>We are happy to hear from our distributors.</p>
            <h3>Business Enquiries</h3>
            <p>For new distributorship, pricing, or order support, please use the <b>Chat on WhatsApp</b> option from the navigation menu, or write to us via the contact details listed in the footer of this website.</p>
            <h3>Hours</h3>
            <p>Monday to Saturday, 9:30 AM – 6:30 PM (IST). Sundays and public holidays closed.</p>
            <h3>Head Office</h3>
            <p>Sri Durga Foods® — Andhra Pradesh, India.</p>
        </InfoPage>
    );
}

// FssaiPage removed — FSSAI feature dropped per product decision.
